import { emitHintSignal } from '@flobi/asset-library/hints';
import { fontName } from '../../ui/fontName';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { DocumentFont } from '@flobi/artwork-core';
import { fontProblems, type FontProblem } from '../../canvas/documentFonts';
import { listFonts, listLibrary, resolveFont, resolveLibraryFamily, type FontFamily, type LibraryFont } from '../../services/fonts';
import { PopUpButton, SegmentedControl, TextField } from '../../ui/controls';
import { useWork } from '../work';
import { Icon } from './icons';
import { FONT_FILTERS, GOOGLE_CATEGORY, flobiSections, tabOf, type FlobiRow, type FontFilter } from './fontPickerGroups';

/**
 * Choosing a font.
 *
 * Two places to choose from, as two tabs. **Flobi Fonts** is what this workspace owns: the
 * fonts of the brand the file is made in first, then the ones its project uses, then the
 * rest of the library — each as a FAMILY, with every weight it has. **Google Fonts** is
 * the catalogue, most used first. One search and one style filter work on both, and while
 * searching each tab says how many it found, so a font is never missed for being in the
 * other one.
 *
 * Picking a family fetches its faces and writes them into the document, which is the
 * moment it becomes part of the file rather than a preference of this browser: everyone
 * who opens it afterwards loads the same faces from the same urls.
 *
 * There was a third heading, "In This File", listing the families the document already
 * carried. It named a place nobody chooses fonts from, and repeated fonts listed above
 * and below it (Tarek, 2026-09-30: "what do you mean by font in this file?"); the font in
 * use is marked where it lives instead, and the picker opens on its tab.
 */

type Tab = 'flobi' | 'google';
const TAB_KEY = 'artwork.fonts.tab';
const KIND_NAME: Record<string, string> = { serif: 'Serif', sans: 'Sans Serif', display: 'Display', script: 'Script', mono: 'Monospace' };

const NO_FONTS: readonly DocumentFont[] = [];

/** The library as last read, kept for the visit: opening the picker again is not an empty list. */
let libraryRead: LibraryFont[] | null = null;

export function FontPicker({
  value,
  inDocument,
  onChoose,
  onClose,
}: {
  /** The family the chosen text is set in. */
  value: string;
  /** What the document already carries, so it can be re-chosen without a round trip. */
  inDocument: readonly DocumentFont[];
  /** A family, and the faces to keep with it when it is new to the document. */
  onChoose: (family: string, font: DocumentFont | null) => void;
  onClose: () => void;
}) {
  const { context, kit } = useWork();
  const [tab, setTab] = useState<Tab>(() => {
    const holding = tabOf(value, inDocument);
    if (holding) return holding;
    try {
      const last = localStorage.getItem(TAB_KEY);
      if (last === 'flobi' || last === 'google') return last;
    } catch {
      // A private window starts on the brand's fonts when there is a brand.
    }
    return kit ? 'flobi' : 'google';
  });
  const chooseTab = (next: Tab) => {
    setTab(next);
    try {
      localStorage.setItem(TAB_KEY, next);
    } catch {
      // Remembered for the visit only.
    }
  };
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FontFilter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Flobi Fonts: read once, then searched and filtered here ──────────────
  const [library, setLibrary] = useState<LibraryFont[] | null>(libraryRead);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    listLibrary()
      .then((fonts) => {
        libraryRead = fonts;
        if (live) setLibrary(fonts);
      })
      .catch((problem) => {
        if (live) setLibraryError((problem as Error).message || 'Flobi Fonts is not answering.');
      });
    return () => {
      live = false;
    };
  }, []);
  const brand = context?.brand ?? null;
  const project = context?.project ?? null;
  const kitFonts = kit && brand && kit.id === brand.id ? kit.fonts : NO_FONTS;
  const sections = useMemo(
    () => flobiSections({ library: library ?? [], kitFonts, brand: brand ? { id: brand.id, name: brand.name } : null, project: project ? { id: project.id, name: project.name } : null, query, filter }),
    [library, kitFonts, brand, project, query, filter],
  );
  const flobiCount = sections.reduce((sum, section) => sum + section.rows.length, 0);

  // ── Google Fonts: asked for as the search and the filter change ──────────
  const [google, setGoogle] = useState<{ items: FontFamily[]; total: number }>({ items: [], total: 0 });
  const [googleState, setGoogleState] = useState<'looking' | 'idle' | 'failed'>('looking');
  const latest = useRef(0);
  useEffect(() => {
    const mine = ++latest.current;
    setGoogleState('looking');
    // A pause before asking, so typing a name is one request rather than six.
    const timer = setTimeout(async () => {
      try {
        const found = await listFonts(query.trim(), filter === 'all' ? undefined : GOOGLE_CATEGORY[filter]);
        if (latest.current !== mine) return;
        setGoogle(found);
        setGoogleState('idle');
      } catch {
        if (latest.current === mine) setGoogleState('failed');
      }
    }, query ? 220 : 0);
    return () => clearTimeout(timer);
  }, [query, filter]);

  /**
   * The newest pick, and whether the picker is still open.
   *
   * A pick waits for its faces, and two quick picks used to be applied in whichever order
   * their downloads finished — the first font could win — and a pick still loading when
   * the picker closed was applied afterwards anyway (QA 2026-09-24, text-F23).
   */
  const pick = useRef(0);
  const open = useRef(true);
  useEffect(() => {
    open.current = true;
    return () => {
      open.current = false;
    };
  }, []);
  /** Families whose faces failed to download, which a pick fetches again (text-F24). */
  useSyncExternalStore(fontProblems.subscribe, fontProblems.version);

  /** One pick: the faces are fetched (unless the file has them), written, and the picker closes. */
  const choose = async (family: string, has: (had: DocumentFont) => boolean, fetch: () => Promise<DocumentFont> | DocumentFont) => {
    const mine = ++pick.current;
    const had = inDocument.find((font) => font.family === family);
    if (had && has(had)) {
      onChoose(family, null);
      fontProblems.retry(family);
      onClose();
      return;
    }
    setBusy(family);
    setError(null);
    try {
      const font = await fetch();
      if (pick.current !== mine || !open.current) return;
      onChoose(font.family, font);
      fontProblems.retry(font.family);
      onClose();
    } catch (problem) {
      if (pick.current === mine && open.current) setError((problem as Error).message || 'That font could not be loaded.');
    } finally {
      if (pick.current === mine && open.current) setBusy(null);
    }
  };
  /** A font picked, as flags: from the file's brand or not, and whether the file has a brand (lib/hints). */
  const told = (fromBrand: boolean) => emitHintSignal('font.picked', { brand: fromBrand, hasBrand: kitFonts.length > 0 });
  const chooseGoogle = (family: string) => {
    told(false);
    void choose(family, (had) => had.source === 'google' || had.source === 'builtin', () => resolveFont(family));
  };
  const chooseFlobi = (row: FlobiRow) => {
    told(row.from === 'kit');
    return chooseFlobiFamily(row);
  };
  const chooseFlobiFamily = (row: FlobiRow) =>
    row.from === 'kit'
      ? // The kit's faces are in hand: written as they are, in case the file holds an older set.
        void choose(row.family, () => false, () => row.font)
      : void choose(row.family, (had) => had.source !== 'google' && had.source !== 'builtin' && had.faces.length >= row.entry.faces.length, () => resolveLibraryFamily(row.family, row.entry.faces));

  /** A family picked before families were gathered is in the file under a face's own name. */
  const shown = fontName(value, inDocument);
  const searching = query.trim() !== '' || filter !== 'all';
  const count = (n: number) => (searching ? ` ${n.toLocaleString()}` : '');

  /** Whether the list runs on below what is shown. */
  const listRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const seeMore = (list: HTMLDivElement) => setMoreBelow(list.scrollTop + list.clientHeight < list.scrollHeight - 4);
  useEffect(() => {
    if (listRef.current) seeMore(listRef.current);
  }, [tab, sections, google.items]);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[8px] p-[10px]">
      <SegmentedControl
        size="small"
        label="Where to choose from"
        value={tab}
        onChange={chooseTab}
        segments={[
          { value: 'flobi', label: `Flobi Fonts${library ? count(flobiCount) : ''}` },
          { value: 'google', label: `Google Fonts${googleState === 'idle' ? count(google.total) : ''}` },
        ]}
      />
      <div className="flex gap-[6px]">
        <TextField className="min-w-0 flex-1" label="Search fonts" icon="search" value={query} onChange={setQuery} placeholder="Search" autoFocus />
        <PopUpButton className="h-[28px] w-[104px] shrink-0 rounded-[9px]" label="Style" value={filter} onChange={setFilter} options={FONT_FILTERS} />
      </div>
      {error && <p className="px-[4px] text-[12px] text-danger">{error}</p>}

      {/* As tall as the window allows, and faded at the foot while more is below: at 300px it held eleven
          families, and a list that stopped at "DM Sans" read as all there was (Farah, 27 Oct). */}
      <div className={`ui-scroll h-[min(440px,calc(100vh-180px))] ${moreBelow ? 'font-list-more' : ''}`} onScroll={(event) => seeMore(event.currentTarget)} ref={listRef}>
        {tab === 'flobi' ? (
          <>
            {sections.map((section) => (
              <div key={section.title ?? 'all'}>
                {section.title ? <p className="truncate px-[6px] pb-[3px] pt-[6px] text-[11px] font-semibold text-label-2">{section.title}</p> : null}
                {section.rows.map((row) => {
                  const usable = row.from === 'kit' || row.entry.faces.some((face) => face.format === 'ttf' || face.format === 'otf');
                  return (
                    <Row
                      key={row.family}
                      family={row.family}
                      note={!usable ? 'Needs TTF or OTF' : row.styles > 1 ? `${row.styles} styles` : row.kind ? KIND_NAME[row.kind] : ''}
                      chosen={row.family === value || row.family === shown}
                      busy={busy === row.family}
                      failed={fontProblems.problemFor(row.family)}
                      onPick={() => chooseFlobi(row)}
                    />
                  );
                })}
              </div>
            ))}
            {flobiCount === 0 ? (
              <Empty>
                {libraryError ?? (library === null ? 'Reading your fonts…' : searching ? 'None of your fonts match.' : 'No fonts in Flobi Fonts yet. The ones you add there, and the ones your brands use, are listed here.')}
                {searching && google.total > 0 && googleState === 'idle' ? <Other onClick={() => chooseTab('google')}>{`${google.total.toLocaleString()} in Google Fonts`}</Other> : null}
              </Empty>
            ) : null}
          </>
        ) : (
          // What is listed belongs to the last question until the new answer lands: dimmed, so it is not read as the answer.
          <div className={googleState === 'looking' && google.items.length > 0 ? 'opacity-50' : undefined}>
            {google.items.map((item) => (
              <Row key={item.family} family={item.family} note={item.category} chosen={item.family === value} busy={busy === item.family} failed={fontProblems.problemFor(item.family)} onPick={chooseGoogle} />
            ))}
            {google.items.length < google.total && googleState === 'idle' ? <p className="px-[6px] py-[6px] text-[12px] text-label-3">{`The first ${google.items.length} of ${google.total.toLocaleString()}. Search to find the rest.`}</p> : null}
            {google.items.length === 0 ? (
              <Empty>
                {googleState === 'looking' ? 'Looking…' : googleState === 'failed' ? 'Google Fonts is not answering.' : 'Nothing by that name.'}
                {googleState === 'idle' && searching && flobiCount > 0 ? <Other onClick={() => chooseTab('flobi')}>{`${flobiCount} in Flobi Fonts`}</Other> : null}
              </Empty>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="grid justify-items-start gap-[8px] px-[6px] py-[8px] text-[12px] leading-[16px] text-label-2">{children}</div>;
}

/** The way to the tab that did find something. */
function Other({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button type="button" onClick={onClick} className="h-[24px] rounded-full bg-fill-2 px-[10px] text-[12px] font-medium text-label hover:bg-fill-selected">
      {children}
    </button>
  );
}

/** What a row that failed says: whether trying again can help, never the server's wording. */
const FONT_PROBLEM_TIP: Record<FontProblem, string> = {
  unavailable: 'This font isn’t available to you, so a fallback is shown.',
  temporary: 'This font didn’t load. Try again in a moment.',
};

function Row({
  family,
  note,
  chosen,
  busy,
  failed = null,
  onPick,
}: {
  family: string;
  note?: string;
  chosen: boolean;
  busy: boolean;
  failed?: FontProblem | null;
  onPick: (family: string) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={() => onPick(family)}
      disabled={busy}
      data-tip={failed ? FONT_PROBLEM_TIP[failed] : undefined}
      className={`flex h-[28px] w-full items-center gap-[8px] rounded-[7px] px-[6px] text-left text-[13px] transition-colors ${
        chosen ? "bg-accent text-on-accent" : "text-label hover:bg-fill-2"
      }`}
    >
      <span className="grid w-[14px] shrink-0 place-items-center">
        {chosen ? <Icon name="check" size={12} /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{family}</span>
      {failed ? (
        <span
          className={`shrink-0 text-[11px] ${chosen ? "text-on-accent/80" : "text-danger"}`}
        >
          Not loaded
        </span>
      ) : (
        <span className={`shrink-0 text-[11px] ${chosen ? "text-on-accent/80" : "text-label-3"}`}>{busy ? "Loading…" : (note ?? "")}</span>
      )}
    </button>
  );
}
